import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Button, Card, Alert, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useNavigate } from 'react-router-dom';
import BudgetControlTable from './metrics/performance/BudgetControlTable';
import budgetService from 'services/budgetService';
import paths from 'routes/paths';
import { toast } from 'react-toastify';
import { useAuth } from 'contexts/AuthContext';

const BudgetControlPerformancePage = () => {
  const navigate = useNavigate();
  const [budgetData, setBudgetData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { filterByAssignedClients } = useAuth();

  const loadBudgetData = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      // 🎯 Usar el nuevo endpoint específico de Performance
      const result = await budgetService.getBudgetControlDataPerformance(filters);
      
      if (result.success) {
        setBudgetData(result.data || []);
      } else {
        setError('Error al cargar los datos de control de presupuestos Performance');
        toast.error('Error al cargar los datos de Performance');
      }
    } catch (error) {
      console.error('Error loading Performance budget data:', error);
      setError('Error de conexión al cargar los datos de Performance');
      toast.error('Error de conexión al cargar los datos de Performance');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBudgetData();
  }, [loadBudgetData]);

  const budgetControlTableData = useMemo(() => {
    // Filtrar por clientes asignados al usuario
    const filteredData = filterByAssignedClients(budgetData, 'clientId');
    
    return filteredData.map(order => {
      const gastado = parseFloat(order.gastado) || 0;
      const deberiaGastar = parseFloat(order.deberiaGastar) || 0;
      const inversionOffset = parseFloat(order.inversionOffset) || 0;
      const porcentajeOffset = parseFloat(order.porcentajeOffset) || 0;

      // Usar las fechas ya formateadas que vienen del backend
      const fechaDesde = order.fechaDesde || 'N/A';
      const fechaHasta = order.fechaHasta || 'N/A';
      const horaDesde = order.horaDesde || 'N/A';
      const horaHasta = order.horaHasta || 'N/A';

      return {
        id: order.id,
        cliente: order.cliente || 'N/A',
        businessUnit: order.businessUnit || 'N/A',
        descripcion: order.descripcion || 'Sin descripción',
        
        fechaDesde: fechaDesde,
        fechaHasta: fechaHasta,
        horaDesde: horaDesde,
        horaHasta: horaHasta,
        
        moneda: order.moneda || 'USD',
        inversionBruta: parseFloat(order.inversionBruta) || 0,
        inversionNeta: parseFloat(order.inversionNeta) || 0,
        asignado: parseFloat(order.asignado) || 0,
        
        deberiaGastar: deberiaGastar,
        gastado: gastado,
        inversionOffset: inversionOffset,
        porcentajeOffset: porcentajeOffset,
        
        duracionDias: parseInt(order.duracionDias) || 0,
        diasTranscurridos: parseInt(order.diasTranscurridos) || 0,
        diasRestantes: parseInt(order.diasRestantes) || 0,
        progresoTemporal: parseFloat(order.progresoTemporal) || 0,
        velocidadGasto: parseFloat(order.velocidadGasto) || 0,
        
        status: order.status || 'unknown',
        lastCalculated: order.lastCalculated,
        
        clientId: order.clientId,
        dateFrom: order.dateFrom,
        dateTo: order.dateTo,
        
        // 🎯 Indicador de tipo de campaña
        campaignType: 'Performance'
      };
    });
  }, [budgetData, filterByAssignedClients]);

  if (loading) {
    return (
      <Container fluid className="px-3">
        <div className="pb-4">
          <div className="mb-4">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon="bullseye" className="me-2 text-primary fs-3" />
              <div>
                <h2 className="mb-1">Control de Presupuestos - Performance</h2>
                <h5 className="text-700 fw-semi-bold">
                  Dashboard especializado para campañas de Performance
                </h5>
              </div>
            </div>
          </div>
          
          <Card className="text-center py-5">
            <Card.Body>
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <h5>Cargando datos de Performance...</h5>
              <p className="text-muted">Obteniendo datos del sistema Performance</p>
            </Card.Body>
          </Card>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="px-3">
        <div className="pb-4">
          <div className="mb-4">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon="bullseye" className="me-2 text-primary fs-3" />
              <div>
                <h2 className="mb-1">Control de Presupuestos - Performance</h2>
                <h5 className="text-700 fw-semi-bold">
                  Dashboard especializado para campañas de Performance
                </h5>
              </div>
            </div>
          </div>
          
          <Alert variant="danger">
            <Alert.Heading>
              <FontAwesomeIcon icon="exclamation-triangle" className="me-2" />
              Error al cargar datos de Performance
            </Alert.Heading>
            <p>{error}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <Button variant="outline-danger" onClick={() => loadBudgetData()}>
                <FontAwesomeIcon icon="redo" className="me-1" />
                Reintentar
              </Button>
            </div>
          </Alert>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="px-3">
      <div className="pb-4">
        <Row className="mb-4">
          <Col xs={12}>
            <div className="p-3 bg-primary bg-opacity-10 rounded-3 border border-primary border-opacity-25">
              <Row className="align-items-center g-3">
                <Col md={6}>
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon icon="bullseye" className="me-3 text-primary fs-2" />
                    <div>
                      <h6 className="mb-1 text-800">
                        <Badge bg="primary" className="me-2">Performance</Badge>
                        Control de Presupuestos
                      </h6>
                      <p className="text-600 mb-0">
                        Gestiona y controla presupuestos de campañas Performance
                      </p>
                      <small className="text-muted">
                        {budgetControlTableData.length} órdenes Performance encontradas
                      </small>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-flex flex-wrap gap-2 justify-content-md-end justify-content-start">

                    <Button 
                      variant="outline-primary" 
                      size="sm"
                      onClick={() => navigate(paths.performanceOrderInsert)}
                      className="flex-shrink-0"
                    >
                      <FontAwesomeIcon icon="cog" className="me-1" />
                      Gestionar Órdenes
                    </Button>
                                        <Button
                      variant="outline-success"
                      size="sm"
                      onClick={() => navigate('/branding/budget-control')} // Link a la vista de Branding
                      className="flex-shrink-0"
                    >
                      <FontAwesomeIcon icon="palette" className="me-1" />
                      Ver Branding
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
        
        <Row>
          <Col xs={12}>
            <BudgetControlTable 
              data={budgetControlTableData} 
              onApplyFilters={loadBudgetData}
              isLoading={loading}
            />
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default BudgetControlPerformancePage; 